import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2, MessageCircle } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import BrandTrustStrip from '../components/BrandTrustStrip';
import { useCassemsCart } from '../contexts/CassemsCartContext';
import { CASSEMS_LANDING, CASSEMS_WA_DUVIDAS_E164, CASSEMS_WA_DUVIDAS_LABEL } from '../content/cassemsLandingCopy';
import type { CassemsCartLine, CassemsCartTotals } from '../contexts/CassemsCartContext';
import { formatBRL } from '../data/catalogoDestaque';
import { createCassemsPedidoPdfArtifact } from '../lib/cassemsPdf';
import { trackPublicEvent } from '../lib/publicAnalytics';
import {
  buildCassemsClientRequestId,
  notifyCassemsPedidoWithRetry,
  type CassemsClientePayload,
  type NotifyCassemsPedidoResult,
} from '../lib/cassemsSubmit';
import type { CassemsPreferenciaPagamento } from '../lib/cassemsWhatsappPedido';
import { hrefWhatsappPedidoCassems } from '../lib/cassemsWhatsappPedido';

function onlyDigits(s: string) {
  return s.replace(/\D/g, '');
}

const PREF_PAGAMENTO: CassemsPreferenciaPagamento = 'equipe_lunins';

type DoneSnapshot = {
  cliente: CassemsClientePayload;
  lines: CassemsCartLine[];
  totals: CassemsCartTotals;
  preferenciaPagamento: CassemsPreferenciaPagamento;
  notificacao: NotifyCassemsPedidoResult;
};

export default function CassemsCheckout() {
  const { lines, totals, clear } = useCassemsCart();
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [cpf, setCpf] = useState('');
  const [setor, setSetor] = useState('');
  const [endereco, setEndereco] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [doneSnapshot, setDoneSnapshot] = useState<DoneSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  const waDuvidasHref = useMemo(
    () =>
      `https://wa.me/${CASSEMS_WA_DUVIDAS_E164}?text=${encodeURIComponent(CASSEMS_LANDING.ctaWhatsappPrefix)}`,
    []
  );

  useEffect(() => {
    document.title = 'Finalizar encomenda — autocuidado Cassems | Lunins';
    trackPublicEvent('cassems_checkout_view', {
      itens_no_carrinho: lines.length,
      total_estimado: totals.subtotalComDesconto,
    });
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const cpfD = onlyDigits(cpf);
    if (cpfD.length < 11) {
      setError('Informe o CPF completo (11 dígitos) para nota fiscal e garantia.');
      return;
    }
    const telD = onlyDigits(telefone);
    if (telD.length < 10) {
      setError('Informe um telefone com DDD.');
      return;
    }
    if (!setor.trim()) {
      setError('Informe seu setor ou área de trabalho.');
      return;
    }

    const cliente = {
      nome: nome.trim(),
      telefone: telefone.trim(),
      cpf: cpf.trim(),
      setor: setor.trim(),
      endereco: endereco.trim(),
    };

    const linesSnapshot = [...lines];
    const totalsSnapshot = { ...totals };
    const pref = PREF_PAGAMENTO;
    const clientRequestId = buildCassemsClientRequestId();

    setSubmitting(true);
    const pdfParams = {
      cliente,
      lines: linesSnapshot,
      totais: totalsSnapshot,
      preferenciaPagamento: pref,
    } as const;

    let pdfArtifact: ReturnType<typeof createCassemsPedidoPdfArtifact>;
    try {
      pdfArtifact = createCassemsPedidoPdfArtifact(pdfParams);
      pdfArtifact.doc.save(pdfArtifact.fileName);
    } catch {
      setSubmitting(false);
      setError(
        'Não foi possível gerar o PDF. Desative bloqueador de pop-ups para este site ou tente outro navegador e confira a pasta de downloads.'
      );
      return;
    }

    const notificacao = await notifyCassemsPedidoWithRetry({
      clientRequestId,
      cliente,
      lines: linesSnapshot,
      totais: totalsSnapshot,
      preferenciaPagamento: pref,
      pdfBase64: pdfArtifact.base64,
      pdfFileName: pdfArtifact.fileName,
    });

    const pedidoRegistrado =
      notificacao.ok || /pedido registrado/i.test(notificacao.ok ? '' : notificacao.message);
    if (!pedidoRegistrado) {
      setSubmitting(false);
      setError(notificacao.message);
      return;
    }

    trackPublicEvent('cassems_order_submitted', {
      itens_no_pedido: linesSnapshot.reduce((acc, item) => acc + item.quantidade, 0),
      produtos_distintos: linesSnapshot.length,
      total_estimado: totalsSnapshot.subtotalComDesconto,
      tem_combo: totalsSnapshot.valorDescontoCombo > 0,
    });

    setDoneSnapshot({
      cliente,
      lines: linesSnapshot,
      totals: totalsSnapshot,
      preferenciaPagamento: pref,
      notificacao,
    });
    clear();
    setDone(true);
    setSubmitting(false);
  }

  if (!done && lines.length === 0) {
    return (
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-16">
          <p className="text-center text-lunins-muted">Seu carrinho está vazio.</p>
          <Link to="/cassems" className="mt-6 text-lunins-accent hover:underline">
            Voltar à ação de bem-estar e escolher equipamentos
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (done && doneSnapshot) {
    const waResumoHref = hrefWhatsappPedidoCassems(
      doneSnapshot.cliente,
      doneSnapshot.lines,
      doneSnapshot.totals,
      doneSnapshot.preferenciaPagamento
    );

    return (
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center px-4 py-12 md:py-16">
          <CheckCircle2 className="h-14 w-14 text-emerald-400" />
          <h1 className="mt-5 text-center font-display text-2xl font-bold text-lunins-ink md:text-3xl">
            Comprovante gerado — próximo passo: WhatsApp
          </h1>
          <p className="mt-3 max-w-lg text-center text-sm leading-relaxed text-lunins-muted">
            O <strong className="text-lunins-ink/80">PDF baixado</strong> é o comprovante do pedido. Envie pelo WhatsApp
            abaixo para a equipe Lunins confirmar valores, entrega e{' '}
            <strong className="text-lunins-ink/80">forma de pagamento</strong> no horário que combinar com você. Nesta
            campanha não há cobrança automática pelo site.
          </p>
          {doneSnapshot.notificacao.ok ? (
            <p className="mt-3 max-w-lg text-center text-sm leading-relaxed text-emerald-800">
              Seu pedido foi registrado e a notificação automática saiu para a operação Lunins, Demétrio e Gabriel.
            </p>
          ) : (
            <p className="mt-3 max-w-lg text-center text-sm leading-relaxed text-amber-300/90">
              Seu pedido foi registrado, mas a automação pediu atenção: {doneSnapshot.notificacao.message}
            </p>
          )}
          {doneSnapshot.notificacao.ok && doneSnapshot.notificacao.warning ? (
            <p className="mt-2 max-w-lg text-center text-sm leading-relaxed text-amber-300/90">
              Aviso da automação: {doneSnapshot.notificacao.warning}
            </p>
          ) : null}
          {doneSnapshot.notificacao.pedidoId ? (
            <p className="mt-2 text-center text-xs text-lunins-muted">Protocolo interno: {doneSnapshot.notificacao.pedidoId}</p>
          ) : null}

          <div className="mt-8 w-full max-w-lg rounded-2xl border border-lunins-border bg-lunins-surface/60 p-5 text-left">
            <p className="text-xs font-semibold uppercase tracking-wide text-lunins-accent">Pagamento</p>
            <p className="mt-3 text-sm leading-relaxed text-lunins-ink/80">
              Tudo é alinhado com a equipe Lunins: horários flexíveis, PIX, link de pagamento ou outra opção que faça
              sentido para você.
            </p>
          </div>

          <div className="mt-8 flex w-full max-w-md flex-col gap-3">
            <a
              href={waResumoHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-emerald-600 py-3.5 text-center text-sm font-bold text-lunins-ink shadow-lg shadow-emerald-900/30 transition hover:bg-emerald-500"
            >
              <MessageCircle className="h-5 w-5 shrink-0" aria-hidden />
              WhatsApp — enviar resumo ao {CASSEMS_WA_DUVIDAS_LABEL.split('·')[0].trim()}
            </a>
            <a
              href={waDuvidasHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full border border-lunins-ink/15 bg-lunins-sand py-3 text-center text-sm font-semibold text-lunins-ink transition hover:bg-lunins-sand"
            >
              <MessageCircle className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
              Só tenho uma dúvida (WhatsApp)
            </a>
          </div>
          <Link to="/cassems" className="mt-10 text-sm text-lunins-accent hover:underline">
            Voltar à ação de bem-estar
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />

      <main className="flex-1 bg-hero-mesh pb-24 pt-8 md:pb-16">
        <div className="mx-auto max-w-lg px-4 sm:px-5">
          <Link
            to="/cassems"
            className="inline-flex min-h-[44px] items-center gap-2 text-sm font-medium text-lunins-muted transition hover:text-lunins-ink"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar ao carrinho
          </Link>

          <h1 className="mt-6 font-display text-2xl font-bold leading-tight text-lunins-ink md:text-3xl">
            Dados para nota fiscal e garantia
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-lunins-muted">
            Ao concluir, você <strong className="text-lunins-muted">baixa o PDF na hora</strong> e segue para o WhatsApp. O
            pagamento é combinado com a equipe e pode seguir por PIX, link de pagamento ou outra forma alinhada no
            atendimento.
          </p>
          <p className="mt-3 rounded-xl border border-lunins-border bg-lunins-surface/40 px-4 py-3 text-sm leading-relaxed text-lunins-muted">
            {CASSEMS_LANDING.checkoutReforco}
          </p>

          <div className="mt-8 rounded-2xl border border-lunins-border bg-lunins-surface/70 p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-lunins-accent">Seu carrinho</p>
            <ul className="mt-3 space-y-2 text-sm text-lunins-ink/80">
              {lines.map((l) => (
                <li key={l.id} className="flex justify-between gap-2">
                  <span>
                    {l.nome} × {l.quantidade}
                  </span>
                  <span className="shrink-0 text-lunins-ink">{formatBRL(l.precoUnitComDesconto * l.quantidade)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 border-t border-lunins-border pt-4 text-xs text-lunins-muted">
              Referência tabela: <span className="text-lunins-muted line-through">{formatBRL(totals.subtotalCheio)}</span>
            </div>
            <div className="mt-2 flex justify-between text-xs text-lunins-muted">
              <span>Subtotal preços da ação</span>
              <span>{formatBRL(totals.subtotalPrecosPromo)}</span>
            </div>
            {totals.valorDescontoCombo > 0 ? (
              <div className="mt-1 flex justify-between text-xs text-emerald-800">
                <span>Combo especial</span>
                <span>−{formatBRL(totals.valorDescontoCombo)}</span>
              </div>
            ) : null}
            <div className="mt-3 flex justify-between font-display text-lg font-bold text-lunins-ink">
              <span>Total estimado</span>
              <span>{formatBRL(totals.subtotalComDesconto)}</span>
            </div>
          </div>

          <form className="mt-8 space-y-4" onSubmit={handleSubmit}>
            <label className="block">
              <span className="text-sm font-medium text-lunins-ink/80">Nome completo</span>
              <input
                required
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className="mt-1.5 min-h-[48px] w-full rounded-xl border border-lunins-border bg-lunins-cream/50 px-4 py-3 text-lunins-ink placeholder:text-lunins-muted focus:border-lunins-accent/50 focus:outline-none focus:ring-2 focus:ring-lunins-accent/20"
                placeholder="Como na documentação"
                autoComplete="name"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-lunins-ink/80">Telefone (WhatsApp)</span>
              <input
                required
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
                className="mt-1.5 min-h-[48px] w-full rounded-xl border border-lunins-border bg-lunins-cream/50 px-4 py-3 text-lunins-ink placeholder:text-lunins-muted focus:border-lunins-accent/50 focus:outline-none focus:ring-2 focus:ring-lunins-accent/20"
                placeholder="67 99999-9999"
                autoComplete="tel"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-lunins-ink/80">CPF</span>
              <input
                required
                value={cpf}
                onChange={(e) => setCpf(e.target.value)}
                className="mt-1.5 min-h-[48px] w-full rounded-xl border border-lunins-border bg-lunins-cream/50 px-4 py-3 text-lunins-ink placeholder:text-lunins-muted focus:border-lunins-accent/50 focus:outline-none focus:ring-2 focus:ring-lunins-accent/20"
                placeholder="Somente números ou formatado"
                autoComplete="off"
              />
              <span className="mt-1 block text-xs text-lunins-muted">Obrigatório para NF-e e garantia do fabricante.</span>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-lunins-ink/80">Setor / área de trabalho</span>
              <input
                required
                value={setor}
                onChange={(e) => setSetor(e.target.value)}
                className="mt-1.5 min-h-[48px] w-full rounded-xl border border-lunins-border bg-lunins-cream/50 px-4 py-3 text-lunins-ink placeholder:text-lunins-muted focus:border-lunins-accent/50 focus:outline-none focus:ring-2 focus:ring-lunins-accent/20"
                placeholder="Ex.: Enfermagem, administrativo, SESMT…"
                autoComplete="organization"
              />
              <span className="mt-1 block text-xs text-lunins-muted">Ajuda a equipe a contextualizar seu atendimento.</span>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-lunins-ink/80">Endereço completo</span>
              <textarea
                required
                rows={3}
                value={endereco}
                onChange={(e) => setEndereco(e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-lunins-border bg-lunins-cream/50 px-4 py-3 text-lunins-ink placeholder:text-lunins-muted focus:border-lunins-accent/50 focus:outline-none focus:ring-2 focus:ring-lunins-accent/20"
                placeholder="Logradouro, número, bairro, cidade, UF, CEP"
              />
              <span className="mt-1 block text-xs text-lunins-muted">Obrigatório para NF-e e entrega</span>
            </label>

            <div className="rounded-xl border border-lunins-border bg-lunins-cream/30 px-4 py-3 text-sm text-lunins-muted">
              <strong className="text-lunins-ink/80">Pagamento:</strong> combinado diretamente com a Lunins após o WhatsApp,
              por PIX, link de pagamento ou outra opção alinhada com você.
            </div>

            {error ? (
              <p className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-300" role="alert">
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className="mt-4 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-lunins-ink py-4 text-base font-semibold text-lunins-cream shadow-glow transition hover:bg-lunins-ink-soft disabled:opacity-50"
            >
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
              {submitting ? 'Gerando PDF e registrando pedido…' : 'Baixar comprovante (PDF) e abrir WhatsApp'}
            </button>
          </form>

          <div className="mt-10">
            <BrandTrustStrip />
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
